import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FiX, FiMenu, FiBarChart2, FiActivity, FiUsers, FiAward, FiBook, FiCode, FiShoppingBag,
  FiTrendingUp, FiGrid, FiZap, FiCopy, FiGift,
} from "react-icons/fi";
import { Link } from "react-router-dom";

import { HeaderLink } from "./HeaderLink";
import "./Header.scss";
import logoImg from "img/logo_MONEY.svg";

type Props = {
  small?: boolean;
  clickCloseIcon?: () => void;
  openSettings?: () => void;
  showRedirectModal: (to: string) => void;
};

type NavItem = { icon: React.ElementType; label: string; desc: string; to?: string; href?: string; qa?: string };

/**
 * Every navigation destination, in one place.
 *
 * Desktop and mobile render this same list through the same row component, so
 * the two can't drift apart. Wallet / chain selector / profile / settings are
 * deliberately NOT here — those stay as persistent controls in the header.
 */
const NAV_GROUPS: { section: string; items: NavItem[] }[] = [
  {
    section: "Trading",
    items: [
      { icon: FiTrendingUp, label: "Trade",     desc: "Perps, TP/SL, TWAP & Scale", to: "/trade",       qa: "trade" },
      { icon: FiGrid,       label: "Dashboard", desc: "Protocol & market overview", to: "/dashboard",   qa: "dashboard" },
      { icon: FiZap,        label: "Earn",      desc: "Provide liquidity & stake",  to: "/earn",        qa: "earn" },
      { icon: FiCopy,       label: "Copy",      desc: "Mirror top traders",         to: "/copytrading", qa: "copytrading" },
      { icon: FiGift,       label: "BESC Draw", desc: "Card packs & prizes",        to: "/card-packs",  qa: "card-packs" },
    ],
  },
  {
    section: "Platform",
    items: [
      { icon: FiShoppingBag, label: "Buy", desc: "Get MONEY & BESC tokens", to: "/buy" },
    ],
  },
  {
    section: "Analytics",
    items: [
      { icon: FiBarChart2, label: "Stats",          desc: "Protocol performance data", href: import.meta.env.VITE_STATS_URL || "https://stats.moneyxpro.com" },
      { icon: FiActivity,  label: "Network Health", desc: "BESC node & RPC status",    to: "/health" },
    ],
  },
  {
    section: "Community",
    items: [
      { icon: FiUsers, label: "Referrals",   desc: "Earn fees by sharing your link", to: "/referrals" },
      { icon: FiAward, label: "Leaderboard", desc: "Top traders ranked by PnL",      to: "/leaderboard" },
    ],
  },
  {
    section: "Developer",
    items: [
      { icon: FiBook, label: "Docs",          desc: "Protocol documentation",         to: "/docs" },
      { icon: FiCode, label: "API Reference", desc: "Integration guides & endpoints", to: "/api-reference" },
    ],
  },
];

function NavRow({
  item,
  onNavigate,
  showRedirectModal,
}: {
  item: NavItem;
  onNavigate: () => void;
  showRedirectModal: (to: string) => void;
}) {
  const Icon = item.icon;
  const inner = (
    <>
      <span className="AppNavMenu__icon"><Icon /></span>
      <span className="AppNavMenu__text">
        <span className="AppNavMenu__label">{item.label}</span>
        <span className="AppNavMenu__desc">{item.desc}</span>
      </span>
    </>
  );

  if (item.href) {
    return (
      <a
        className="AppNavMenu__item"
        role="menuitem"
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onNavigate}
      >
        {inner}
      </a>
    );
  }

  return (
    <HeaderLink
      className="AppNavMenu__item"
      qa={item.qa ?? item.label.toLowerCase().replace(/\s+/g, "-")}
      to={item.to!}
      showRedirectModal={showRedirectModal}
      onClick={onNavigate}
    >
      {inner}
    </HeaderLink>
  );
}

function NavSections({
  onNavigate,
  showRedirectModal,
}: {
  onNavigate: () => void;
  showRedirectModal: (to: string) => void;
}) {
  return (
    <>
      {NAV_GROUPS.map((group) => (
        <React.Fragment key={group.section}>
          <div className="AppNavMenu__section">{group.section}</div>
          {group.items.map((item) => (
            <NavRow key={item.label} item={item} onNavigate={onNavigate} showRedirectModal={showRedirectModal} />
          ))}
        </React.Fragment>
      ))}
    </>
  );
}

/**
 * Desktop: the one and only nav menu, behind a hamburger on the right, beside
 * the wallet/chain/settings controls. Uses position:fixed so it escapes the
 * header's stacking and overflow context.
 */
export function AppMenuDropdown({ showRedirectModal }: { showRedirectModal: (to: string) => void }) {
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // The trigger sits on the left (just after the logo), so anchor the panel's
  // left edge to it — right-anchoring would open it off the left of the screen.
  const calcPos = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setPanelStyle({
      position: "fixed",
      top: Math.round(r.bottom + 8),
      left: Math.round(r.left),
      zIndex: 9999,
    });
  }, []);

  const closeMenu = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => {
    if (open) closeMenu();
    else {
      calcPos();
      setOpen(true);
    }
  }, [open, calcPos, closeMenu]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: PointerEvent) => {
      const outside =
        (!panelRef.current || !panelRef.current.contains(e.target as Node)) &&
        (!triggerRef.current || !triggerRef.current.contains(e.target as Node));
      if (outside) closeMenu();
    };
    document.addEventListener("pointerdown", handler, true);
    return () => document.removeEventListener("pointerdown", handler, true);
  }, [open, closeMenu]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, closeMenu]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("scroll", calcPos, true);
    window.addEventListener("resize", calcPos);
    return () => {
      window.removeEventListener("scroll", calcPos, true);
      window.removeEventListener("resize", calcPos);
    };
  }, [open, calcPos]);

  return (
    <div className="AppNavMenu">
      <button
        ref={triggerRef}
        className={`AppNavMenu__trigger${open ? " is-open" : ""}`}
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Menu"
        title="Menu"
      >
        {open ? <FiX className="AppNavMenu__burger" /> : <FiMenu className="AppNavMenu__burger" />}
      </button>

      {open && (
        <div ref={panelRef} className="AppNavMenu__panel" role="menu" style={panelStyle}>
          <NavSections onNavigate={closeMenu} showRedirectModal={showRedirectModal} />
        </div>
      )}
    </div>
  );
}

export function AppHeaderLinks({ small, clickCloseIcon, showRedirectModal }: Props) {
  // Desktop nav lives entirely in AppMenuDropdown (rendered by Header, on the
  // right next to the wallet controls), so the header bar stays clean.
  if (!small) return null;

  const close = () => clickCloseIcon?.();

  return (
    <div className="App-header-links AppNavMenu AppNavMenu--drawer">
      <div className="App-header-links-header">
        <Link className="App-header-link-main" to="/" onClick={close}>
          <img src={logoImg} alt="MONEY Logo" />
        </Link>
        <div className="App-header-menu-icon-block mobile-cross-menu" onClick={close}>
          <FiX className="App-header-menu-icon" />
        </div>
      </div>

      <NavSections onNavigate={close} showRedirectModal={showRedirectModal} />
    </div>
  );
}
